import { useState, useMemo } from 'react';
import { Search, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { useUsers } from '../../hooks/useUsers';
import type { AdminUser } from '../../services/adminApi';

interface UserTableProps {
  onSelectUser: (user: AdminUser) => void;
}

type SortKey = 'name' | 'email' | 'status' | 'role' | 'last_login';
type SortDir = 'asc' | 'desc';

const ROLES = ['', 'platform_admin', 'parent_org_admin', 'customer_admin', 'portfolio_manager', 'site_operator', 'analyst', 'site_viewer'];

function getUserName(u: AdminUser) {
  return u.first_name ? `${u.first_name} ${u.last_name || ''}`.trim().toLowerCase() : '';
}

export function UserTable({ onSelectUser }: UserTableProps) {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const { data, isLoading } = useUsers({ search: search || undefined, role: roleFilter || undefined, status: statusFilter || undefined });

  const rawUsers: AdminUser[] = data?.data?.users || [];

  const users = useMemo(() => {
    if (!sortKey) return rawUsers;
    const sorted = [...rawUsers].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'name': cmp = getUserName(a).localeCompare(getUserName(b)); break;
        case 'email': cmp = a.email.localeCompare(b.email); break;
        case 'status': cmp = (a.is_active === b.is_active ? 0 : a.is_active ? -1 : 1); break;
        case 'role': cmp = a.role.localeCompare(b.role); break;
        case 'last_login': {
          const aTime = a.last_login ? new Date(a.last_login).getTime() : 0;
          const bTime = b.last_login ? new Date(b.last_login).getTime() : 0;
          cmp = aTime - bTime;
          break;
        }
      }
      return sortDir === 'desc' ? -cmp : cmp;
    });
    return sorted;
  }, [rawUsers, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ column }: { column: SortKey }) => {
    if (sortKey !== column) return <ChevronsUpDown className="w-3 h-3 text-ems-text-muted" />;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
  };

  return (
    <div className="bg-ems-bg-card rounded-2xl border border-ems-border">
      {/* Filters */}
      <div className="p-4 border-b border-ems-border flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ems-text-muted" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search users..."
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-ems-border text-sm text-ems-text-primary bg-ems-bg placeholder:text-ems-text-muted outline-none focus:border-ems-green focus:ring-2 focus:ring-ems-green/20"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border border-ems-border text-sm text-ems-text-secondary bg-ems-bg outline-none focus:border-ems-green"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border border-ems-border text-sm text-ems-text-secondary bg-ems-bg outline-none focus:border-ems-green"
        >
          <option value="">All Roles</option>
          {ROLES.filter(Boolean).map((r) => (
            <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-ems-border">
              {([['name', 'User'], ['email', 'Email'], ['status', 'Status'], ['role', 'Role'], ['last_login', 'Last Login']] as [SortKey, string][]).map(([key, label]) => (
                <th
                  key={key}
                  onClick={() => handleSort(key)}
                  className="text-left px-4 py-3 text-xs font-semibold text-ems-text-muted uppercase tracking-wider cursor-pointer hover:text-ems-text-primary select-none"
                >
                  <span className="inline-flex items-center gap-1">
                    {label}
                    <SortIcon column={key} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <tr key={i} className="border-b border-ems-border">
                  <td colSpan={5} className="px-4 py-3"><div className="h-4 bg-ems-bg-card-hover rounded animate-pulse" /></td>
                </tr>
              ))
            ) : users.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-ems-text-muted">No users found</td></tr>
            ) : (
              users.map((user) => {
                const initials = `${(user.first_name || user.email)[0]}${(user.last_name || '')[0] || ''}`.toUpperCase();
                return (
                  <tr
                    key={user.user_id}
                    onClick={() => onSelectUser(user)}
                    className={`border-b border-ems-border hover:bg-ems-bg-card-hover cursor-pointer transition-colors ${!user.is_active ? 'opacity-60' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${user.is_active ? 'bg-ems-green/10 text-ems-green' : 'bg-ems-bg-card-hover text-ems-text-muted'}`}>
                          {initials}
                        </div>
                        <span className={`font-medium ${user.is_active ? 'text-ems-text-primary' : 'text-ems-text-muted'}`}>
                          {user.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : '—'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-ems-text-secondary">{user.email}</td>
                    <td className="px-4 py-3">
                      {user.is_active ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-ems-green/10 text-xs font-medium text-ems-green">
                          <span className="w-1.5 h-1.5 rounded-full bg-ems-green" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-ems-bg-card-hover text-xs font-medium text-ems-text-muted">
                          <span className="w-1.5 h-1.5 rounded-full bg-ems-text-muted" />
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-block px-2 py-0.5 rounded bg-ems-bg-card-hover text-xs font-medium text-ems-text-secondary capitalize">
                        {user.role.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-ems-text-muted">
                      {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
